// ./src/components/features/Connectors/ConfigEditors/NotionConnectorConfigEditor.tsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Stack,
  TextInput,
  Text,
  Anchor,
  Alert,
  Code,
  Switch,
  Group,
  Box,
  List,
} from "@mantine/core";
import {
  IconInfoCircle,
  IconNotes,
  IconAlertTriangle,
} from "@tabler/icons-react";
import { stringifyObject, parseObject } from "@/utils";
import { NotionConnectorConfig } from "@/types";

export interface NotionConnectorConfigEditorProps {
  /** JSON string of connector configuration */
  value: string;

  /** Callback when configuration changes */
  onChange: (value: string) => void;

  /** Whether the editor is disabled */
  disabled?: boolean;
}

// Default empty configuration
const DEFAULT_CONFIG: NotionConnectorConfig = {
  integrationToken: "",
  databaseId: "",
  includeMetadata: true,
  titleProperty: "",
};

/**
 * Parse and validate Notion connector configuration
 */
const parseNotionConfig = (jsonString: string): NotionConnectorConfig => {
  const parsed = parseObject(jsonString, DEFAULT_CONFIG);
  return parsed;
};

export const NotionConnectorConfigEditor: React.FC<
  NotionConnectorConfigEditorProps
> = ({ value, onChange, disabled = false }) => {
  // Parse incoming value to config state
  const [config, setConfig] = useState<NotionConnectorConfig>(() =>
    parseNotionConfig(value)
  );

  // Sync config when external value changes
  useEffect(() => {
    const parsed = parseNotionConfig(value);
    setConfig(parsed);
  }, [value]);

  // Update parent when config changes
  const updateConfig = useCallback(
    (newConfig: NotionConnectorConfig) => {
      setConfig(newConfig);
      onChange(stringifyObject(newConfig, DEFAULT_CONFIG));
    },
    [onChange]
  );

  // Handle field changes
  const handleIntegrationTokenChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, integrationToken: e.target.value });
  };

  const handleDatabaseIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, databaseId: e.target.value });
  };

  const handleTitlePropertyChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, titleProperty: e.target.value });
  };

  const handleIncludeMetadataChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, includeMetadata: e.currentTarget.checked });
  };

  return (
    <Stack gap="md">
      {/* Setup Instructions */}
      <Alert icon={<IconNotes size={16} />} color="gray" variant="light">
        <Text size="sm" fw={500} mb="xs">
          Setup Instructions:
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>
            Go to{" "}
            <Anchor
              href="https://www.notion.so/my-integrations"
              target="_blank"
              rel="noopener noreferrer"
            >
              notion.so/my-integrations
            </Anchor>
          </List.Item>
          <List.Item>Click "New integration" and give it a name</List.Item>
          <List.Item>Copy the "Internal Integration Token"</List.Item>
          <List.Item>
            Open your Notion database, click <strong>...</strong> →{" "}
            <strong>Connections</strong> → Add your integration
          </List.Item>
        </List>
      </Alert>

      {/* Integration Token Field */}
      <TextInput
        label="Integration Token"
        placeholder="secret_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
        description="Internal Integration Token from Notion"
        value={config.integrationToken}
        onChange={handleIntegrationTokenChange}
        disabled={disabled}
        required
        size="md"
        type="password"
        autoComplete="off"
      />

      {/* Database ID Field */}
      <TextInput
        label="Database ID"
        placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
        description="Found in the database URL"
        value={config.databaseId}
        onChange={handleDatabaseIdChange}
        disabled={disabled}
        required
        size="md"
      />

      {/* Help for Database ID */}
      <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
        <Text size="sm" fw={500} mb={4}>
          Where to find the Database ID:
        </Text>
        <Text size="xs">Open database as full page. In the URL:</Text>
        <Text size="xs" mt={4}>
          <Code>
            https://notion.so/workspace/<strong>DATABASE_ID</strong>?v=...
          </Code>
        </Text>
        <Text size="xs" mt={4}>
          The ID is the 32-character string (without hyphens).
        </Text>
      </Alert>

      {/* Title Property Field */}
      <TextInput
        label="Title Property Name"
        placeholder="Name"
        description="Database property to use as page title (leave empty to auto-detect)"
        value={config.titleProperty || ""}
        onChange={handleTitlePropertyChange}
        disabled={disabled}
        size="md"
      />

      {/* Toggle Options */}
      <Box>
        <Group justify="space-between">
          <div>
            <Text size="sm">Include Metadata</Text>
            <Text size="xs" c="dimmed">
              Map Timestamp, Submission ID, View URL to database properties
            </Text>
          </div>
          <Switch
            checked={config.includeMetadata !== false}
            onChange={handleIncludeMetadataChange}
            disabled={disabled}
          />
        </Group>
      </Box>

      {/* How It Works */}
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        <Text size="sm" fw={500} mb="xs">
          How it works:
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>
            Form fields are matched to database properties by{" "}
            <strong>label first</strong>, then by <strong>field name</strong>
          </List.Item>
          <List.Item>
            Each submission creates a new page/row in the database
          </List.Item>
          <List.Item>
            Property types are auto-detected (text, number, date, email, etc.)
          </List.Item>
          <List.Item>Hidden fields and security fields are excluded</List.Item>
        </List>
      </Alert>

      {/* Database Setup Tips */}
      <Alert
        icon={<IconAlertTriangle size={16} />}
        color="orange"
        variant="light"
      >
        <Text size="sm" fw={500} mb="xs">
          Database Setup Tips:
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>
            Name database properties to match your form field{" "}
            <strong>labels</strong> or <strong>field names</strong>
          </List.Item>
          <List.Item>
            Example: form field with label "Email Address" or name "email" →
            Notion property "Email Address" or "email"
          </List.Item>
          <List.Item>
            Use appropriate property types: Text for text fields, Number for
            numbers, Email for emails, etc.
          </List.Item>
          <List.Item>
            For metadata, add properties named: "Timestamp" (Date), "Submission
            ID" (Text), "View URL" (URL)
          </List.Item>
        </List>
      </Alert>
    </Stack>
  );
};
